import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LoginFacade } from 'mbs-ui-shared@facades/login.facade';
import { ConfigurationService, EnvironmentService } from 'mbs-ui-shared@services';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  private noUnauthorizedCheckList: string[];

  private get isAgentLocal(): boolean {
    return this.auth.isAgentLocal;
  }

  constructor(
    private auth: AuthFacade,
    private loginFacade: LoginFacade,
    private configService: ConfigurationService,
    private environment: EnvironmentService
  ) {
    this.noUnauthorizedCheckList = [
      'api/auth/new-token-scope/feedback-access',
      '/api/version',
      'api/auth/new-token-scope/appsbackup-access',
      'api/odata/Mbs',
      'api/auth/login/refresh'
    ];
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.addToken(this.auth.authToken, req);

    this.noUnauthorizedCheckList = this.noUnauthorizedCheckList.concat(
      this.configService.loaded ? ['/commands/invokepscommand', '/commands/invokeactivecommand'] : []
    );

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || (this.isAgentLocal && req.url.includes('api/auth/logout')) || this.noUnauthorizedCheckList.some((x) => req.url.includes(x))) {
          // rethrow to next interceptor
          return throwError(() => error);
        }

        const refreshAndRetry = this.auth.tryRefreshToken$().pipe(
          switchMap((refreshed) => {
            if (!refreshed) {
              this.loginFacade.logout();

              return throwError(() => error);
            }

            req = this.addToken(this.auth.authToken, req);
            this.auth.fetchCurrentUser$.subscribe();

            return next.handle(req).pipe(
              catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                  this.loginFacade.logout();
                }

                return throwError(() => error);
              })
            );
          })
        );

        if (this.environment.production) {
          // for external service that required auth by JWT
          if (this.environment.useSessionToken) {
            return refreshAndRetry;
          }

          this.loginFacade.logout();

          return throwError(() => error);
        } else {
          return refreshAndRetry;
        }
      })
    );
  }

  private addToken(tokenData: string, req: HttpRequest<any>): HttpRequest<any> {
    const excludeHosts = this.configService?.loaded ? [this.configService.get('appsBaseHref')] : [];

    if (req.headers.get('CustomAuth')) {
      return req;
    } else if (excludeHosts.some((host) => req.url.includes(host))) {
      return req;
    } else if (tokenData) {
      req = req.clone({ headers: req.headers.set('Authorization', this.auth.authToken) });
    }

    return req;
  }
}
